import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { loadPhotoshootPhotos } from '../services/dataService';

const PhotoshootDetail = ({ siteData }) => {
    const { photoshootId } = useParams();
    const [photos, setPhotos] = useState([]);
    const [photoshoot, setPhotoshoot] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedPhoto, setSelectedPhoto] = useState(null);

    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);

                // Find photoshoot details
                const shootData = siteData?.photography?.photoshoots?.find(p => p.id === photoshootId);
                setPhotoshoot(shootData);

                // Load photos for this photoshoot
                const photoshootPhotos = await loadPhotoshootPhotos(photoshootId);
                setPhotos(photoshootPhotos);
            } catch (error) {
                console.error('Failed to load photoshoot:', error);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [photoshootId, siteData]);

    const handlePhotoClick = (photo) => {
        setSelectedPhoto(photo);
    };

    const closeModal = () => {
        setSelectedPhoto(null);
    };

    const navigatePhoto = (direction) => {
        const currentIndex = photos.findIndex(p => p.id === selectedPhoto.id);
        let newIndex;

        if (direction === 'next') {
            newIndex = currentIndex === photos.length - 1 ? 0 : currentIndex + 1;
        } else {
            newIndex = currentIndex === 0 ? photos.length - 1 : currentIndex - 1;
        }

        setSelectedPhoto(photos[newIndex]);
    };

    if (loading) {
        return (
            <div className="pt-16 min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sage-600"></div>
            </div>
        );
    }

    if (!photoshoot) {
        return (
            <div className="pt-16 min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <h1 className="text-2xl font-bold text-gray-900 mb-4">Photoshoot Not Found</h1>
                    <Link to="/photography" className="text-sage-600 hovertext-sage-700">
                        Back to Photography
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="pt-16 min-h-screen bg-white">
            {/* Header */}
            <div className="py-12 bg-gray-50">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <Link
                        to="/photography"
                        className="inline-flex items-center text-sage-600 hovertext-sage-700 mb-6"
                    >
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        Back to Photography
                    </Link>

                    <h1 className="text-4xl font-light text-gray-900 mb-4">
                        {photoshoot.title}
                    </h1>

                    {photoshoot.description && (
                        <p className="text-xl text-gray-600 mb-4">
                            {photoshoot.description}
                        </p>
                    )}

                    <div className="flex items-center justify-center space-x-4 text-sm text-gray-500">
                        <span>{photoshoot.date}</span>
                        <span>•</span>
                        <span>{photos.length} photos</span>
                        {photoshoot.location && (
                            <>
                                <span>•</span>
                                <span>{photoshoot.location}</span>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Photo Showcase - Single column, large photos */}
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="space-y-12">
                    {photos.map((photo, index) => (
                        <div
                            key={photo.id}
                            className="group cursor-pointer"
                            onClick={() => handlePhotoClick(photo)}
                        >
                            <div className="relative bg-gray-100 rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300">
                                <img
                                    src={photo.url}
                                    alt={photo.title}
                                    className="w-full h-auto max-h-[80vh] object-contain group-hover:scale-[1.02] transition-transform duration-500"
                                    style={{ minHeight: '400px' }}
                                />

                                {/* Photo info overlay */}
                                {(photo.title || photo.description) && (
                                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                        {photo.title && (
                                            <h3 className="text-xl font-medium mb-2">{photo.title}</h3>
                                        )}
                                        {photo.description && (
                                            <p className="text-sm opacity-90">{photo.description}</p>
                                        )}
                                    </div>
                                )}

                                {/* Photo number indicator */}
                                <div className="absolute top-4 left-4 bg-black/50 text-white px-2 py-1 rounded text-sm">
                                    {index + 1} of {photos.length}
                                </div>
                            </div>

                            {/* Photo info below image (always visible) */}
                            {(photo.title || photo.description) && (
                                <div className="mt-4 text-center">
                                    {photo.title && (
                                        <h3 className="text-lg font-medium text-gray-900 mb-2">{photo.title}</h3>
                                    )}
                                    {photo.description && (
                                        <p className="text-gray-600">{photo.description}</p>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {photos.length === 0 && (
                    <div className="text-center py-20">
                        <p className="text-gray-500 text-lg">No photos in this photoshoot yet.</p>
                    </div>
                )}
            </div>

            {/* Lightbox Modal */}
            {selectedPhoto && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center z-50 p-4"
                    onClick={closeModal}
                >
                    <div className="relative max-w-full max-h-full w-full flex items-center justify-center">
                        {/* Navigation buttons */}
                        {photos.length > 1 && (
                            <>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        navigatePhoto('prev');
                                    }}
                                    className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 text-4xl z-10 bg-black/30 rounded-full w-12 h-12 flex items-center justify-center"
                                >
                                    ‹
                                </button>

                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        navigatePhoto('next');
                                    }}
                                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 text-4xl z-10 bg-black/30 rounded-full w-12 h-12 flex items-center justify-center"
                                >
                                    ›
                                </button>
                            </>
                        )}

                        {/* Close button */}
                        <button
                            onClick={closeModal}
                            className="absolute top-4 right-4 text-white hover:text-gray-300 text-3xl z-10 bg-black/30 rounded-full w-10 h-10 flex items-center justify-center"
                        >
                            ×
                        </button>

                        {/* Image */}
                        <img
                            src={selectedPhoto.url}
                            alt={selectedPhoto.title}
                            className="max-w-full max-h-full object-contain"
                            onClick={(e) => e.stopPropagation()}
                        />

                        {/* Photo info */}
                        {(selectedPhoto.title || selectedPhoto.description) && (
                            <div className="absolute bottom-0 left-0 right-0 bg-black/80 text-white p-6 text-center">
                                {selectedPhoto.title && (
                                    <h3 className="text-xl font-medium mb-2">{selectedPhoto.title}</h3>
                                )}
                                {selectedPhoto.description && (
                                    <p className="text-sm opacity-90">{selectedPhoto.description}</p>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default PhotoshootDetail;